import { useRef, useState } from 'react';
import { Box, IconButton, Theme, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Controller, useFormContext } from 'react-hook-form';
import { makeStyles } from 'tss-react/mui';
import useSnackBar from '../../hooks/useSnackBar';

const useStyles = makeStyles<{
  disabled?: boolean;
}>()((theme: Theme, { disabled }) => ({
  uploadBox: {
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '12px',
    padding: '24px',
    textAlign: 'center',
    background: 'rgba(255,255,255,0.03)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: '0.2s ease',
    opacity: disabled ? 0.6 : 1,

    '&:hover': {
      background: disabled ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.06)',
    },
  },
  uploadIcon: {
    fontSize: '40px',
    color: theme.palette.info.main,
    marginBottom: '8px',
  },
  uploadText: {
    color: theme.palette.common.white,
    fontWeight: 500,
    fontSize: '16px',
    fontFamily: 'Inter',
  },
  uploadSubText: {
    color: theme.palette.text.secondary,
    fontSize: '13px',
    fontFamily: 'Inter',
    marginTop: '6px',
  },
  fileList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    maxHeight: '220px',
    overflowY: 'auto',
    marginTop: '16px',
  },
  fileItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'rgba(255,255,255,0.05)',
    borderRadius: '10px',
    padding: '10px 12px',
    gap: '12px',
  },
  fileInfo: {
    minWidth: 0,
    flex: 1,
  },
  fileName: {
    color: theme.palette.common.white,
    fontSize: '14px',
    fontWeight: 500,
    fontFamily: 'Inter',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  fileMeta: {
    color: theme.palette.text.secondary,
    fontSize: '12px',
    fontFamily: 'Inter',
    marginTop: '2px',
  },
  hiddenInput: {
    display: 'none',
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: '12px',
    marginTop: '8px',
    fontFamily: 'Inter',
  },
}));

interface FormMultiFileUploadFieldProps {
  name: string;
  inputId: string;
  uploadText?: string;
  uploadSubText?: string;
  acceptedTypes?: string[];
  acceptedExtensionsText?: string;
  maxFileSize?: number;
  disabled?: boolean;
}

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const FormMultiFileUploadField = ({
  name,
  inputId,
  uploadText = 'Click to upload or drag and drop',
  uploadSubText,
  acceptedTypes = [],
  acceptedExtensionsText = 'PDF, DOC, DOCX, JPG, JPEG, PNG',
  maxFileSize = 10 * 1024 * 1024,
  disabled = false,
}: FormMultiFileUploadFieldProps) => {
  const { setAlert } = useSnackBar();
  const { control, setValue } = useFormContext();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const styles = useStyles({ disabled });

  const validateFiles = (files: File[]) => {
    const validFiles: File[] = [];

    for (const file of files) {
      if (acceptedTypes.length && !acceptedTypes.includes(file.type)) {
        setAlert((prev) => ({
          ...prev,
          message: `${file.name} has an unsupported file type.`,
          type: 'error',
        }));
        continue;
      }

      if (file.size > maxFileSize) {
        setAlert((prev) => ({
          ...prev,
          message: `${file.name} exceeds the ${Math.round(maxFileSize / (1024 * 1024))}MB limit.`,
          type: 'error',
        }));
        continue;
      }

      validFiles.push(file);
    }

    return validFiles;
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={[]}
      render={({ field: { value = [], onChange }, fieldState: { error } }) => {
        const files: File[] = Array.isArray(value) ? value : [];

        const addFiles = (incomingFiles: FileList | File[]) => {
          const filesArray = Array.from(incomingFiles);
          const validFiles = validateFiles(filesArray);

          const merged = [...files, ...validFiles];
          const unique = merged.filter(
            (file, index, self) =>
              index ===
              self.findIndex(
                (f) =>
                  f.name === file.name &&
                  f.size === file.size &&
                  f.lastModified === file.lastModified,
              ),
          );

          onChange(unique);
          setValue(name, unique, { shouldDirty: true, shouldValidate: true });

          if (inputRef.current) {
            inputRef.current.value = '';
          }
        };

        const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          if (event.target.files?.length) {
            addFiles(event.target.files);
          }
        };

        const handleRemoveFile = (fileToRemove: File) => {
          const updatedFiles = files.filter(
            (file) =>
              !(
                file.name === fileToRemove.name &&
                file.size === fileToRemove.size &&
                file.lastModified === fileToRemove.lastModified
              ),
          );

          onChange(updatedFiles);
          setValue(name, updatedFiles, { shouldDirty: true, shouldValidate: true });
        };

        const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
          event.preventDefault();
          event.stopPropagation();
          setIsDragging(false);

          if (disabled) return;

          if (event.dataTransfer.files?.length) {
            addFiles(event.dataTransfer.files);
          }
        };

        return (
          <Box>
            <input
              ref={inputRef}
              id={inputId}
              type="file"
              multiple
              className={styles.classes.hiddenInput}
              accept={acceptedTypes.join(',')}
              onChange={handleInputChange}
              disabled={disabled}
            />

            <Box
              className={styles.classes.uploadBox}
              onClick={() => !disabled && inputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                if (!disabled) setIsDragging(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setIsDragging(false);
              }}
              onDrop={handleDrop}
              sx={{
                borderColor: isDragging ? 'info.main' : 'secondary.main',
                background: isDragging ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.03)',
              }}
            >
              <UploadFileIcon className={styles.classes.uploadIcon} />
              <Typography className={styles.classes.uploadText}>{uploadText}</Typography>
              <Typography className={styles.classes.uploadSubText}>
                {uploadSubText || `${acceptedExtensionsText} - Max ${Math.round(maxFileSize / (1024 * 1024))}MB each`}
              </Typography>
            </Box>

            {!!files.length && (
              <Box className={styles.classes.fileList}>
                {files.map((file) => (
                  <Box
                    key={`${file.name}-${file.size}-${file.lastModified}`}
                    className={styles.classes.fileItem}
                  >
                    <Box className={styles.classes.fileInfo}>
                      <Typography className={styles.classes.fileName}>{file.name}</Typography>
                      <Typography className={styles.classes.fileMeta}>
                        {formatFileSize(file.size)}
                      </Typography>
                    </Box>

                    <IconButton
                      onClick={() => handleRemoveFile(file)}
                      sx={{ color: 'white' }}
                      size="small"
                      disabled={disabled}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            {error?.message && (
              <Typography className={styles.classes.errorText}>
                {error.message}
              </Typography>
            )}
          </Box>
        );
      }}
    />
  );
};

export default FormMultiFileUploadField;